import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FORM_URL = 'https://readdy.ai/api/form/d9pep41n2trhb35gve4g';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    const formData = new FormData(form);
    formData.delete('website_alt');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEO
        title="关于我们 - 韩漫网站 - 好看的韩国漫画免费阅读平台"
        description="韩漫网站品牌介绍 - 我们致力于为中文读者提供优质的韩国漫画免费阅读服务。了解我们的品牌故事、用户口碑和联系方式。"
        keywords="韩漫网站,关于我们,韩漫,韩国漫画,联系我们,品牌介绍"
        canonicalUrl="https://www.manhwa-site.com/about"
      />
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
        </div>

        <section className="relative w-full h-[350px] md:h-[420px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20illustration%20of%20a%20cozy%20reading%20space%20with%20bookshelves%20filled%20with%20colorful%20comic%20books%2C%20soft%20warm%20lighting%2C%20comfortable%20reading%20chair%2C%20creative%20workspace%20atmosphere%2C%20artistic%20lifestyle%20scene%2C%20warm%20amber%20and%20cream%20tones&width=1600&height=700&seq=about-hero-manhwa-01&orientation=landscape"
            alt="韩漫网站 - 关于我们"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-3xl md:text-4xl font-bold text-white font-heading">关于我们</h1>
            <p className="mt-3 text-base text-white/80 max-w-lg mx-auto">了解韩漫网站的品牌故事与使命</p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14" aria-labelledby="brand-title">
          <div className="max-w-3xl mx-auto">
            <h2 id="brand-title" className="text-2xl md:text-3xl font-bold text-foreground-800 font-heading text-center mb-6">品牌介绍</h2>
            <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
              <p>
                <strong>韩漫网站</strong>成立于2020年，是国内领先的<strong>韩国漫画</strong>在线阅读平台。我们致力于为广大中文读者提供高质量、正版的<strong>韩漫</strong>阅读体验，让每一位漫画爱好者都能轻松畅享精彩的<strong>韩国漫画</strong>世界。
              </p>
              <p>
                我们的团队由一群热爱<strong>漫画</strong>的年轻人组成，汇集了内容编辑、技术开发、设计制作等多个领域的专业人才。凭借对<strong>韩漫</strong>文化的深刻理解和对用户需求的精准把握，我们精心筛选和整理了涵盖浪漫爱情、奇幻冒险、都市生活、悬疑推理、动作热血、治愈日常、古风历史、BL/GL等全品类<strong>韩国漫画</strong>作品。
              </p>
              <p>
                我们坚信，优质的<strong>漫画</strong>内容应该被更多人看到。因此，<strong>韩漫网站</strong>始终坚持免费阅读的理念，让每一位读者都能无门槛地享受<strong>韩国漫画</strong>带来的乐趣。同时，我们也非常注重阅读体验的优化——简洁清爽的界面设计、流畅快速的加载速度、舒适护眼的阅读模式，都是我们不断追求的目标。
              </p>
              <p>
                截至2026年，<strong>韩漫网站</strong>已收录超过500部<strong>韩国漫画</strong>作品，累计服务超过500万读者。未来，我们将继续拓展内容资源，优化产品体验，努力成为中文世界最受欢迎的<strong>韩漫</strong>阅读平台。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-14 h-14 flex items-center justify-center mx-auto rounded-full bg-accent-100 mb-4">
                  <i className="ri-book-open-line text-2xl text-accent-600"></i>
                </span>
                <p className="text-3xl font-bold text-accent-500 font-heading">500+</p>
                <p className="text-sm text-foreground-500 mt-1">精选韩漫作品</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-14 h-14 flex items-center justify-center mx-auto rounded-full bg-primary-100 mb-4">
                  <i className="ri-user-smile-line text-2xl text-primary-600"></i>
                </span>
                <p className="text-3xl font-bold text-primary-500 font-heading">500万+</p>
                <p className="text-sm text-foreground-500 mt-1">累计服务读者</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-14 h-14 flex items-center justify-center mx-auto rounded-full bg-secondary-100 mb-4">
                  <i className="ri-thumb-up-line text-2xl text-secondary-600"></i>
                </span>
                <p className="text-3xl font-bold text-secondary-500 font-heading">98%</p>
                <p className="text-sm text-foreground-500 mt-1">用户好评率</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100/60 py-14" aria-labelledby="reputation-title">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 id="reputation-title" className="text-2xl md:text-3xl font-bold text-foreground-800 font-heading text-center mb-10">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  name: '小雨',
                  role: '大学生',
                  avatar: 'https://readdy.ai/api/search-image?query=A%20simple%20profile%20avatar%20illustration%20of%20a%20young%20Asian%20woman%20with%20warm%20smile%2C%20minimalist%20flat%20design%2C%20soft%20warm%20colors%2C%20clean%20background&width=80&height=80&seq=avatar-user-01&orientation=squarish',
                  text: '韩漫网站的漫画资源真的太丰富了！我最喜欢浪漫爱情类的韩漫，在这里找到了好多好看的韩国漫画，画风细腻，剧情也超棒。每天睡前必刷！',
                },
                {
                  name: '大明',
                  role: '上班族',
                  avatar: 'https://readdy.ai/api/search-image?query=A%20simple%20profile%20avatar%20illustration%20of%20a%20young%20Asian%20man%20with%20friendly%20expression%2C%20minimalist%20flat%20design%2C%20soft%20warm%20colors%2C%20clean%20background&width=80&height=80&seq=avatar-user-02&orientation=squarish',
                  text: '作为通勤族，手机上看韩漫特别方便。网站加载速度快，界面简洁好操作。奇幻冒险类的漫画是我最爱，追了好几部连载，更新也及时。',
                },
                {
                  name: '小艺',
                  role: '设计师',
                  avatar: 'https://readdy.ai/api/search-image?query=A%20simple%20profile%20avatar%20illustration%20of%20a%20young%20Asian%20woman%20with%20artistic%20vibe%2C%20minimalist%20flat%20design%2C%20soft%20warm%20colors%2C%20clean%20background&width=80&height=80&seq=avatar-user-03&orientation=squarish',
                  text: '作为一个漫画爱好者，我真的很欣赏这个网站的设计。分类清晰，搜索方便，而且免费阅读这一点太良心了。安利给了身边好多朋友！',
                },
              ].map((user, i) => (
                <div key={i} className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{user.name}</p>
                      <p className="text-xs text-foreground-400">{user.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{user.text}</p>
                  <div className="flex items-center gap-0.5 mt-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-xs text-primary-400"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14" aria-labelledby="contact-title">
          <div className="max-w-2xl mx-auto">
            <h2 id="contact-title" className="text-2xl md:text-3xl font-bold text-foreground-800 font-heading text-center mb-6">联系我们</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              如果您有任何问题、建议或合作意向，欢迎通过以下表单与我们联系。我们会在1-2个工作日内回复您。
            </p>

            {formStatus === 'success' ? (
              <div className="text-center py-10 bg-background-50 rounded-lg border border-background-200/70">
                <span className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-accent-100 mb-4">
                  <i className="ri-check-line text-3xl text-accent-600"></i>
                </span>
                <h3 className="text-lg font-semibold text-foreground-800 mb-2">提交成功！</h3>
                <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快与您联系。</p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-4 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  继续留言
                </button>
              </div>
            ) : (
              <form
                data-readdy-form="true"
                id="contact-form"
                onSubmit={handleSubmit}
                className="space-y-5 bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8"
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">您的姓名 <span className="text-primary-500">*</span></label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700 placeholder:text-foreground-300 transition-all duration-200"
                    placeholder="请输入您的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">电子邮箱 <span className="text-primary-500">*</span></label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700 placeholder:text-foreground-300 transition-all duration-200"
                    placeholder="请输入您的电子邮箱"
                  />
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700 placeholder:text-foreground-300 transition-all duration-200"
                    placeholder="请输入主题"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容 <span className="text-primary-500">*</span></label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700 placeholder:text-foreground-300 transition-all duration-200 resize-none"
                    placeholder="请输入您的留言内容（不超过500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <input type="text" name="website_alt" className="honeypot-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  发送留言
                </button>

                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 text-center">{formError}</p>
                )}
              </form>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
              <div className="text-center p-4 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center mx-auto rounded-full bg-accent-100 mb-2">
                  <i className="ri-mail-line text-accent-600"></i>
                </span>
                <p className="text-xs text-foreground-500">电子邮箱</p>
                <p className="text-sm font-medium text-foreground-700">contact@manhwa-site.com</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center mx-auto rounded-full bg-primary-100 mb-2">
                  <i className="ri-wechat-line text-primary-600"></i>
                </span>
                <p className="text-xs text-foreground-500">微信公众号</p>
                <p className="text-sm font-medium text-foreground-700">韩漫网</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-10 h-10 flex items-center justify-center mx-auto rounded-full bg-secondary-100 mb-2">
                  <i className="ri-time-line text-secondary-600"></i>
                </span>
                <p className="text-xs text-foreground-500">工作时间</p>
                <p className="text-sm font-medium text-foreground-700">周一至周五 9:00-18:00</p>
              </div>
            </div>
          </div>
        </section>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          'name': '关于我们 - 韩漫网站',
          'description': '韩漫网站品牌介绍 - 致力于为中文读者提供优质的韩国漫画免费阅读服务',
          'url': 'https://www.manhwa-site.com/about',
          'isPartOf': { '@type': 'WebSite', 'name': '韩漫网站', 'url': 'https://www.manhwa-site.com' },
        })}} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.manhwa-site.com/' },
            { '@type': 'ListItem', 'position': 2, 'name': '关于我们', 'item': 'https://www.manhwa-site.com/about' },
          ],
        })}} />
      </main>

      <Footer />
    </>
  );
}